import { useMemo, useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTASection } from "../components/CTASection";
import { Reveal } from "../components/Reveal";
import { GALLERY, GALLERY_CATEGORIES } from "../data/content";

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i > 0 ? i - 1 : items.length - 1)), [items.length]);
  const next = useCallback(() => setLightbox((i) => (i < items.length - 1 ? i + 1 : 0)), [items.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, close, prev, next]);

  return (
    <main data-testid="gallery-page">
      <PageHeader
        title="Photo Gallery"
        subtitle="Documentary moments from our health camps, programs and community activities across Assam."
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="container-hecso">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  filter === c
                    ? "bg-hecso-green text-white border-hecso-green"
                    : "bg-white text-hecso-ink border-slate-200 hover:border-hecso-green hover:text-hecso-green"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
            {items.map((g, i) => (
              <Reveal key={`${g.caption}-${i}`} delay={(i % 3) * 60}>
                <button
                  className="group relative block w-full overflow-hidden rounded-xl text-left"
                  onClick={() => setLightbox(i)}
                  data-testid={`gallery-item-${i}`}
                >
                  <img src={g.src} alt={g.caption} className="h-64 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/35" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-hecso-orange">{g.category}</span>
                    <span className="block text-sm font-medium text-white">{g.caption}</span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightbox !== null && items[lightbox] && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={close}
          data-testid="lightbox"
        >
          <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2" onClick={close} aria-label="Close" data-testid="lightbox-close">
            <X className="w-7 h-7" />
          </button>
          <button
            className="absolute left-3 sm:left-6 text-white/80 hover:text-white p-2"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-9 h-9" />
          </button>
          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={items[lightbox].src.replace("w=1200", "w=1600")} alt={items[lightbox].caption} className="max-h-[75vh] w-full object-contain rounded-lg" />
            <figcaption className="mt-3 text-center text-sm text-white/80" data-testid="lightbox-caption">
              <span className="font-semibold text-hecso-orange">{items[lightbox].category}</span> — {items[lightbox].caption}
            </figcaption>
          </figure>
          <button
            className="absolute right-3 sm:right-6 text-white/80 hover:text-white p-2"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-9 h-9" />
          </button>
        </div>
      )}

      <CTASection />
    </main>
  );
}
